/**
 * Инструменты, которые модель может вызывать в редакторе.
 *
 * Все исполняются на клиенте: источник истины для файлов — состояние
 * редактора, поэтому модель и UI не расходятся.
 */

export interface ToolCall {
  id: string;
  type: "function";
  function: { name: string; arguments: string };
}

export const TOOL_DEFS = [
  {
    type: "function",
    function: {
      name: "list_files",
      description: "List file paths in the current paste.",
      parameters: { type: "object", properties: {}, additionalProperties: false },
    },
  },
  {
    type: "function",
    function: {
      name: "read_file",
      description: "Read the full contents of a file from the paste.",
      parameters: {
        type: "object",
        properties: { path: { type: "string", description: "File path, e.g. main.js" } },
        required: ["path"],
        additionalProperties: false,
      },
    },
  },
  {
    type: "function",
    function: {
      name: "write_file",
      description:
        "Create a file or replace its whole contents. Prefer this over returning code in chat when you want the change applied.",
      parameters: {
        type: "object",
        properties: {
          path: { type: "string" },
          content: { type: "string" },
        },
        required: ["path", "content"],
        additionalProperties: false,
      },
    },
  },
  {
    type: "function",
    function: {
      name: "delete_file",
      description: "Delete a file from the paste.",
      parameters: {
        type: "object",
        properties: { path: { type: "string" } },
        required: ["path"],
        additionalProperties: false,
      },
    },
  },
  {
    type: "function",
    function: {
      name: "get_console_errors",
      description:
        "Return recent console.error/console.warn output and uncaught errors from the live preview. " +
        "Use this to debug runtime problems instead of asking the user to copy errors.",
      parameters: {
        type: "object",
        properties: {
          limit: {
            type: "integer",
            description: "How many recent entries to return (default 50, max 200)",
            minimum: 1,
            maximum: 200,
          },
        },
        additionalProperties: false,
      },
    },
  },
  {
    type: "function",
    function: {
      name: "set_title",
      description:
        "Set the paste title. Use it when the user asks for a name or when the purpose of the paste becomes clear.",
      parameters: {
        type: "object",
        properties: { title: { type: "string", description: "New title, up to 200 chars" } },
        required: ["title"],
        additionalProperties: false,
      },
    },
  },
  {
    type: "function",
    function: {
      name: "execute_js",
      description:
        "Evaluate JavaScript inside the live preview iframe (sandboxed, no access to the editor). " +
        "Use for debugging: inspect DOM, read variables, measure things. " +
        "Body of an async function: use `return <value>` to send a result back, `await` is allowed.",
      parameters: {
        type: "object",
        properties: {
          code: { type: "string", description: "JavaScript to evaluate in the preview page" },
        },
        required: ["code"],
        additionalProperties: false,
      },
    },
  },
] as const;

export const MAX_TOOL_RESULT = 32 * 1024;

/** Ограничения повторяют серверные. */
export const LIMITS = {
  maxFiles: 20,
  maxFileBytes: 256 * 1024,
  maxPasteBytes: 1024 * 1024,
};

export function toolResult(text: string): string {
  return text.length > MAX_TOOL_RESULT
    ? `${text.slice(0, MAX_TOOL_RESULT)}\n…[обрезано, всего ${text.length} символов]`
    : text;
}

export function normalizePath(raw: string): string | null {
  const p = raw.replace(/\\/g, "/").replace(/^\/+/, "").trim();
  if (!p) return null;
  if (p.split("/").some((s) => s === "" || s === "..")) return null;
  return p;
}

export function byteLength(s: string): number {
  return new TextEncoder().encode(s).length;
}